import { css } from "lit";

export const toolbarResponsiveStyles = css`
  :host {
    position: relative;
    z-index: 999999;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 0.65;
    }
  }

  .keyboard-hints {
    display: flex;
    justify-content: center;
    gap: 16px;
    opacity: 0.65;
    font-size: 0.75rem;
    padding: 6px 12px;
    user-select: none;
    color: var(--wa-color-text-secondary, #aaa);
  }

  /* ------------------------------------------------------------
     KEYBOARD HINTS
  ------------------------------------------------------------ */

  .keyboard-hints strong {
    font-weight: 600;
    color: var(--wa-color-text-primary, #fff);
    padding: 2px 6px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.08);
    backdrop-filter: blur(4px);
  }

  /* Hide keyboard hints on touch devices */
  @media (pointer: coarse) {
    .keyboard-hints {
      display: none;
    }
    .layout-name {
      display: none;
    }
  }

  /* ------------------------------------------------------------
     TOOLBAR
  ------------------------------------------------------------ */
  .toolbar {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    border-radius: 16px;
    backdrop-filter: blur(18px) saturate(160%);
    background: rgba(20, 20, 30, 0.45);
    animation: fadeIn 1000ms ease-out;
  }

  .core-actions {
    display: flex;
    justify-content: center;
    gap: 12px;
  }

  .full-actions {
    display: none;
  }

  wa-button[circle] {
    color: rgba(190, 190, 190, 0.8);
    --wa-button-radius: 50%;
    --wa-icon-size: 20px;
  }

  wa-button[circle].active {
    color: unset;
  }

  .layout-name {
    margin-left: 6px;
  }

  wa-icon.active {
    color: var(--wa-accent, #4da3ff);
  }

  /* ------------------------------------------------------------
     TRANSITION SELECTOR
  ------------------------------------------------------------ */
  .transition-selector-wrapper {
    position: relative;
    z-index: 1000000;
  }

  .transition-menu {
    position: absolute;
    top: 44px;
    right: 0;
    background: rgba(25, 25, 35, 0.9);
    backdrop-filter: blur(18px);
    border-radius: 12px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    z-index: 1000001;
  }

  .transition-option {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    border-radius: 8px;
    cursor: pointer;
    transition: background 120ms ease;
  }

  .transition-option:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .transition-option.active {
    background: rgba(80, 150, 255, 0.25);
  }

  .transition-option .label {
    text-transform: capitalize;
  }

  .swatch {
    width: 24px;
    height: 16px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.15);
  }

  .swatch.soft-glass {
    backdrop-filter: blur(6px);
  }
  .swatch.slide {
    background: linear-gradient(to right, #ffffff22, #ffffffaa);
  }
  .swatch.radial {
    background: radial-gradient(circle, #ffffffaa, #ffffff22);
  }
  .swatch.wipe {
    background: linear-gradient(to right, #ffffffaa 50%, #ffffff22 50%);
  }
  .swatch.noise {
    background: repeating-linear-gradient(
      45deg,
      #ffffff33 0,
      #ffffff33 2px,
      #00000033 2px,
      #00000033 4px
    );
  }
  .swatch.film-burn {
    background: linear-gradient(45deg, #ff8a00aa, #ff0044aa);
  }

  /* ------------------------------------------------------------
     BACKDROP
  ------------------------------------------------------------ */
  .sheet-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(4px);
    z-index: 999998;
    pointer-events: auto;
    touch-action: none;
  }

  /* ------------------------------------------------------------
     BOTTOM SHEET
  ------------------------------------------------------------ */
  .sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;

    height: var(--sheet-height, 60px);
    max-height: 90vh;

    background: rgba(25, 25, 35, 0.9);
    backdrop-filter: blur(20px);
    border-radius: 20px 20px 0 0;

    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-y;
    pointer-events: auto;

    transition: none;
    z-index: 999999;

    display: block;
    padding: 0;
  }

  .sheet.snap {
    transition: height 220ms ease-out;
  }

  .sheet.dragging {
    overflow: hidden;
  }

  /* ------------------------------------------------------------
     DRAG ZONE (LARGE TOUCH TARGET)
  ------------------------------------------------------------ */
  .sheet-dragzone {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;

    touch-action: none;
    pointer-events: auto;
  }

  .sheet-handle {
    width: 48px;
    height: 6px;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 3px;
  }

  /* ------------------------------------------------------------
     CONTENT
  ------------------------------------------------------------ */
  .sheet-content {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    touch-action: pan-y;
  }

  .sheet h3 {
    margin: 4px 0 6px;
    font-size: 0.9rem;
    opacity: 0.8;
  }

  /* ------------------------------------------------------------
     MOBILE INTERVAL SLIDER
  ------------------------------------------------------------ */
  .interval-slider-wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 4px 0;
  }

  .interval-slider-wrapper input[type="range"] {
    width: 100%;
    height: 32px;
    -webkit-appearance: none;
    background: transparent;
    touch-action: pan-y;
  }

  .interval-slider-wrapper input[type="range"]::-webkit-slider-runnable-track {
    height: 6px;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 3px;
  }

  .interval-slider-wrapper input[type="range"]::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: white;
    margin-top: -10px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    touch-action: none;
  }

  .interval-value {
    font-size: 0.9rem;
    opacity: 0.8;
  }

  /* ------------------------------------------------------------
     RESPONSIVE
  ------------------------------------------------------------ */
  @media (min-width: 720px) {
    .toolbar {
      flex-direction: row;
      align-items: center;
    }

    .core-actions .more-btn {
      display: none;
    }

    .full-actions {
      display: flex;
      gap: 12px;
      flex: 1;
      justify-content: flex-end;
      align-items: center;
    }

    .interval-slider-wrapper {
      display: none;
    }
  }

  @media (pointer: coarse) {
    .layout-name {
      display: none;
    }
  }

  /* Interval Popup */
  .interval-wrapper {
    position: relative; /* anchor for popup */
  }

  .interval-popup {
    position: absolute;
    top: 100%; /* directly below the button */
    right: 0;
    z-index: 9999; /* top of the stack */
    display: none;
    width: 200px;
    padding: 12px 16px;
    margin-top: 6px;
    border-radius: 12px;

    background: var(--wa-surface-1, rgba(255, 255, 255, 0.15));
    backdrop-filter: blur(20px);
  }

  .interval-popup[visible] {
    display: block;
  }
`;
